import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { CheckSquare, RotateCcw, Mail, Send, Loader2, FileText, Calendar, Edit, Save, AlertTriangle, MessageSquare, Phone } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import EditableStageEmailModal from './EditableStageEmailModal';
import LinkDeliveryModal from './LinkDeliveryModal';

// ==================== App Link Modal (Solar Exit — Send Application Link) ====================

const AppLinkModal = ({ open, onClose, clientId, client, onSent }) => {
  const [extension, setExtension] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && client) {
      setToEmail(client.email || '');
      setSubject('Your Application Link – Easy Quick Fund');
      setBody(`Dear ${client.full_name || '[Client Name]'},

Please use the link below to complete your application:

https://www.easyquickfund.com/apply

Thank you from the Quick Fund Team

If you have any questions, please reply to this email and one of our team members will contact you with more information.

Important:
Your credit report must be unfrozen and unlocked.

If you believe your credit is frozen or locked, please contact:

Experian: (888) 397-3742
TransUnion: (800) 916-8800

Websites:
www.easyquickfund.com
www.prestigioustravelclub.org

Phone:
757-448-3705`);
    }
  }, [open, client]);

  const handleSend = async () => {
    const ext = extension.trim();
    if (!ext || !/^\d{3,5}$/.test(ext)) {
      toast.error('Enter a valid extension (3-5 digits)');
      return;
    }
    if (!toEmail.trim()) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/send-app-link`, { rep_extension: ext, to_email: toEmail.trim(), subject: subject.trim(), body: body.replace(/\n/g, '<br>'), confirm: true });
      toast.success('Application link sent successfully');
      setExtension('');
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send application link');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="app-link-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Send className="h-4 w-4 text-blue-600" />
            Send Application Link
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">To</Label>
            <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@email.com" className="text-sm" data-testid="app-link-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm" data-testid="app-link-subject" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Your Extension (required)</Label>
            <Input
              value={extension}
              onChange={(e) => setExtension(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="e.g. 1234"
              className="text-sm w-32"
              maxLength={5}
              data-testid="app-link-extension-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">3-5 digit extension included in the email</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Email Body (editable)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="text-sm leading-relaxed" data-testid="app-link-body" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="app-link-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={sending || !extension.trim() || !toEmail.trim()} data-testid="app-link-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending...' : 'Send Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== FALLBACK STAGES (used when no config available) ====================

const FALLBACK_STAGES = [
  { key: 'pending_onboarding', label: 'Pending Onboarding / Welcome Call', type: 'checkbox', enabled: true, has_reason: true },
  { key: 'onboarding_emails_sent', label: 'Onboarding Emails Sent to Client', type: 'checkbox', enabled: true, has_reason: true },
  { key: 'onboarding_call_complete', label: 'Onboarding Call Complete', type: 'checkbox', enabled: true, has_reason: true },
  { key: 'all_documents_completed', label: 'Client Completed All Documents', type: 'checkbox', enabled: true, has_reason: true, has_follow_up: true },
  { key: 'second_attempt_documents', label: '2nd Attempt | All Documents Complete', type: 'checkbox', enabled: true, has_reason: true, has_follow_up: true },
  { key: 'send_ach_email', label: 'Send ACH Email to Client', type: 'checkbox', enabled: true },
  { key: 'bank_reviewing_documents', label: 'Bank is Reviewing Documents', type: 'checkbox', enabled: true, has_reason: true },
  { key: 'funds_deposited', label: 'Funds Deposited into Client Account', type: 'checkbox', enabled: true, has_reason: true, has_deposit_date: true, is_final: true, maps_to_stage: 'funded' },
];

// ==================== ACH Email Modal ====================

const ACH_LINK = 'https://www.easyquickfund.com/ach';

function buildACHEmailBody(client) {
  const clientName = client?.full_name || '[Client Name]';
  const companyName = client?.company_name || '[Company Name]';
  const achAmount = client?.deal_amount || client?.requested_amount || 0;
  const formattedAmount = achAmount ? `$${Number(achAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$[Amount]';

  return `Dear ${clientName},

Thank you for choosing ${companyName} for your services. We are writing to inform you that your account has been approved and is ready for processing.

The expected ACH amount for your account is ${formattedAmount}. To complete the ACH authorization process, please visit the link below:

${ACH_LINK}

Please ensure the following when completing the form:
- Use the bank account you would like the ACH to be drafted from
- Double-check all routing and account numbers for accuracy
- Complete and submit the form at your earliest convenience

If you have any questions or require assistance, please do not hesitate to reach out to our support team.

Best regards,
${companyName}
Client Support Team`;
}

const ACHEmailModal = ({ open, onClose, client, clientId, onSent }) => {
  const clientEmail = client?.email || '';
  const companyName = client?.company_name || '';
  const [toEmail, setToEmail] = useState(clientEmail);
  const [subject, setSubject] = useState(`ACH Authorization Required - ${companyName}`);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && client) {
      setToEmail(client.email || '');
      setSubject(`ACH Authorization Required - ${client.company_name || ''}`);
      setBody(buildACHEmailBody(client));
    }
  }, [open, client]);

  const handleSend = async () => {
    if (!toEmail.trim()) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      const htmlBody = body.replace(/\n/g, '<br>');
      await api.post(`/clients/${clientId}/send-ach-email`, {
        to_email: toEmail.trim(), subject: subject.trim(), body: htmlBody, confirm: true,
      });
      toast.success(`ACH email sent to ${toEmail}`);
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ach-email-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-blue-600" />
            Send ACH Email to Client
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">To</Label>
            <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@email.com" className="text-sm" data-testid="ach-email-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm" data-testid="ach-email-subject" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Email Body</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} className="text-sm font-mono leading-relaxed" data-testid="ach-email-body" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="ach-email-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={sending} data-testid="ach-email-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Disclosure Email Modal ====================

const DisclosureEmailModal = ({ open, onClose, clientId, client, onSent }) => {
  const clientEmail = client?.email || '';
  const companyName = client?.company_name || '';
  const [toEmail, setToEmail] = useState(clientEmail);
  const [subject, setSubject] = useState(`Disclosure Notice - ${companyName}`);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && clientId) {
      setToEmail(client?.email || '');
      setSubject(`Disclosure Notice - ${client?.company_name || ''}`);
      setLoading(true);
      setError(null);
      api.get(`/clients/${clientId}/disclosure-script`)
        .then(res => { setBody(res.data.rendered_text || ''); })
        .catch(() => { setError('No disclosure script configured'); setBody(''); })
        .finally(() => { setLoading(false); });
    }
  }, [open, clientId, client]);

  const handleSend = async () => {
    if (!toEmail.trim()) { toast.error('Recipient email is required'); return; }
    if (!body.trim()) { toast.error('Message body is required'); return; }
    setSending(true);
    try {
      const htmlBody = body.replace(/\n/g, '<br>');
      await api.post(`/clients/${clientId}/send-disclosure-email`, {
        to_email: toEmail.trim(), subject: subject.trim(), body: htmlBody, confirm: true,
      });
      toast.success(`Disclosure email sent to ${toEmail}`);
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send disclosure email');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="disclosure-email-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-amber-600" />
            Send Disclosure to Client
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">To</Label>
            <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@email.com" className="text-sm" data-testid="disclosure-email-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm" data-testid="disclosure-email-subject" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Email Body (editable)</Label>
            {loading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600" data-testid="disclosure-email-no-script">{error}</div>
            ) : (
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="text-sm font-mono leading-relaxed" data-testid="disclosure-email-body" />
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="disclosure-email-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={sending || !!error || !body.trim()} data-testid="disclosure-email-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending...' : 'Send Disclosure'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Disclosure Script Popup Modal ====================

const DisclosureScriptModal = ({ open, onClose, clientId, client, onConfirm, isInternal }) => {
  const [loading, setLoading] = useState(true);
  const [scriptData, setScriptData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && clientId) {
      setLoading(true);
      setError(null);
      setEditing(false);
      api.get(`/clients/${clientId}/disclosure-script`)
        .then(res => {
          setScriptData(res.data);
          setEditText(res.data.script_text || '');
        })
        .catch(() => { setError('No disclosure script configured'); })
        .finally(() => { setLoading(false); });
    }
  }, [open, clientId]);

  const handleSaveEdit = async () => {
    if (!scriptData?.template_id || !editText.trim()) return;
    setSaving(true);
    try {
      await api.put(`/disclosure-templates/${scriptData.template_id}`, { script_text: editText });
      // Refetch to get re-rendered text
      const res = await api.get(`/clients/${clientId}/disclosure-script`);
      setScriptData(res.data);
      setEditing(false);
      toast.success('Script updated');
    } catch { toast.error('Failed to save script'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="disclosure-script-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4 text-amber-600" />
            Disclosure Script
          </DialogTitle>
        </DialogHeader>
        <div className="py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600" data-testid="disclosure-no-script-error">{error}</div>
          ) : editing ? (
            <div className="space-y-2">
              <p className="text-[10px] text-slate-500">Use {'{{client_name}}'}, {'{{company_name}}'}, {'{{deal_amount}}'}, {'{{funded_amount}}'} as placeholders.</p>
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={14}
                className="text-sm leading-relaxed font-mono"
                data-testid="disclosure-edit-textarea"
              />
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-[10px] text-amber-600 font-semibold uppercase tracking-wider mb-2">Read to client:</p>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="disclosure-script-text">
                {scriptData?.rendered_text}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel Edit</Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="disclosure-save-edit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save Script
              </Button>
            </>
          ) : (
            <>
              {isInternal && !error && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="disclosure-edit-btn">
                  <Edit className="h-3 w-3 mr-1" /> Edit Script
                </Button>
              )}
              <Button variant="outline" onClick={onClose} data-testid="disclosure-cancel-btn">Cancel</Button>
              <Button onClick={onConfirm} disabled={!!error} className="bg-emerald-600 hover:bg-emerald-700" data-testid="disclosure-confirm-btn">
                <CheckSquare className="h-4 w-4 mr-1" />
                Client Confirmed
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Stage SMS Modal ====================

const StageSMSModal = ({ open, onClose, client, clientId, stage, onSent }) => {
  const clientPhone = client?.phone || '';
  const [toPhone, setToPhone] = useState(clientPhone);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && client && stage) {
      setToPhone(client.phone || '');
      let tpl = stage.sms_template || `Hi [Client Name], this is [Company Name]. `;
      tpl = tpl.replace(/\[Client Name\]/g, client.full_name || '')
               .replace(/\[Company Name\]/g, client.company_name || '');
      setBody(tpl);
    }
  }, [open, client, stage]);

  const handleSend = async () => {
    if (!toPhone.trim()) { toast.error('Phone number is required'); return; }
    if (!body.trim()) { toast.error('Message body is required'); return; }
    setSending(true);
    try {
      await api.post('/deal-automation/sms/send', {
        client_id: clientId,
        message: body.trim(),
      });
      toast.success(`SMS sent to ${toPhone}`);
      if (onSent) onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="stage-sms-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4 text-green-600" />
            Send SMS — {stage?.label || 'SMS'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">To Phone</Label>
            <Input value={toPhone} onChange={(e) => setToPhone(e.target.value)} placeholder="Phone number" className="text-sm" data-testid="stage-sms-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Message (editable)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="text-sm leading-relaxed" data-testid="stage-sms-body" />
            <p className="text-[10px] text-slate-400 mt-1">{body.length} characters</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="stage-sms-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={sending} className="bg-green-600 hover:bg-green-700" data-testid="stage-sms-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending...' : 'Send SMS'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Stage Email Modal ====================

const StageEmailModal = ({ open, onClose, client, clientId, stage, onSent }) => {
  const clientEmail = client?.email || '';
  const [toEmail, setToEmail] = useState(clientEmail);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && client && stage) {
      setToEmail(client.email || '');
      let subj = stage.email_subject_template || `${stage.label} — ${client.company_name || ''}`;
      subj = subj.replace(/\[Client Name\]/g, client.full_name || '')
                  .replace(/\[Company Name\]/g, client.company_name || '');
      setSubject(subj);
      let tpl = stage.email_template || `Dear ${client.full_name || '[Client Name]'},\n\n\n\nBest regards,\n${client.company_name || '[Company Name]'}`;
      tpl = tpl.replace(/\[Client Name\]/g, client.full_name || '')
               .replace(/\[Company Name\]/g, client.company_name || '');
      setBody(tpl);
    }
  }, [open, client, stage]);

  const handleSend = async () => {
    if (!toEmail.trim()) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      const htmlBody = body.replace(/\n/g, '<br>');
      await api.post(`/clients/${clientId}/send-ach-email`, {
        to_email: toEmail.trim(), subject: subject.trim(), body: htmlBody, confirm: true,
      });
      toast.success(`Email sent to ${toEmail}`);
      if (onSent) onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stage-email-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="h-4 w-4 text-blue-600" />
            Send Email — {stage?.label || 'Email'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">To</Label>
            <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@email.com" className="text-sm" data-testid="stage-email-to" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm" data-testid="stage-email-subject" />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Email Body (editable)</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="text-sm font-mono leading-relaxed" data-testid="stage-email-body" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="stage-email-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={sending} data-testid="stage-email-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Three-Way Selector ====================

const ThreeWaySelector = ({ value, onChange, disabled }) => {
  const options = ['Yes', 'No', 'N/A'];
  // v3.15-931: "Booked" implies Yes for the selector's visual state — the
  // "Booked ✓" badge next to the label carries the finer-grained detail.
  const effective = value === 'Booked' ? 'Yes' : value;
  return (
    <div className="flex items-center gap-1" data-testid="three-way-selector">
      {options.map((opt) => {
        const isActive = effective === opt;
        const colorClass = opt === 'Yes'
          ? (isActive ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-200 text-slate-500 hover:border-emerald-300')
          : opt === 'No'
          ? (isActive ? 'bg-red-500 text-white border-red-500' : 'border-slate-200 text-slate-500 hover:border-red-300')
          : (isActive ? 'bg-slate-500 text-white border-slate-500' : 'border-slate-200 text-slate-500 hover:border-slate-400');
        return (
          <button
            key={opt}
            onClick={() => !disabled && onChange(isActive ? null : opt)}
            disabled={disabled}
            className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-all ${colorClass} ${disabled ? 'opacity-50 cursor-default' : 'cursor-pointer'}`}
            data-testid={`three-way-${opt.toLowerCase().replace('/', '')}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
};

// ==================== Main Component ====================

// v3.15-933: short "time ago" formatter. Inputs an ISO timestamp, returns a
// compact label suitable for a "Sent X ago" stamp ("just now", "5m ago",
// "3h ago", "2d ago"). Falls back to a calendar date for older items.
const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60)        return 'just now';
  if (diffSec < 3600)      return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400)     return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const DealStagesChecklist = ({ clientId, isInternal, client, companyIndustry }) => {
  // v3.15-779: pull current user for CC auto-fill in editable email modal
  const { user: authUser } = useAuth();
  const currentUserEmail = authUser?.email || '';
  const [stageData, setStageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [achEmailOpen, setAchEmailOpen] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [activePopupStage, setActivePopupStage] = useState(null);
  const [configStages, setConfigStages] = useState(null);
  const [managerAlert, setManagerAlert] = useState(null);
  // Stage SMS/Email modal state
  const [stageSmsOpen, setStageSmsOpen] = useState(false);
  const [stageEmailOpen, setStageEmailOpen] = useState(false);
  // v3.15-779: editable stage-email modal (Fee Disclosure / App Link / Disclosure ACH)
  const [editableEmailOpen, setEditableEmailOpen] = useState(false);
  const [editableEmailAction, setEditableEmailAction] = useState(null);
  const [appLinkOpen, setAppLinkOpen] = useState(false);
  // v3.21-1775 — Client Link Delivery modal state.
  // Value is the requested link_type ('credit_application' |
  // 'ach_disclosure' | 'appointment') while the modal is open, or
  // null when closed.
  const [linkDeliveryLinkType, setLinkDeliveryLinkType] = useState(null);
  // v3.21-1785 · Welcome SMS composer state — opens when the operator
  // clicks Send Welcome / Resend Welcome on the welcome_message_sent
  // stage.  On successful `POST /api/clients/{id}/send-welcome-sms`
  // we flip the stage to Yes via the existing onStageChange callback.
  const [welcomeSmsOpenForStage, setWelcomeSmsOpenForStage] = useState(null);
  const [welcomeSmsMessage, setWelcomeSmsMessage] = useState('');
  const [welcomeSmsSending, setWelcomeSmsSending] = useState(false);
  // v3.21-1787 · Server-side preview state — populated by
  // GET /api/clients/{id}/welcome-sms/preview when the composer opens.
  const [welcomeSmsPreviewLoading, setWelcomeSmsPreviewLoading] = useState(false);
  const [welcomeSmsPreviewError, setWelcomeSmsPreviewError] = useState(null);
  const [activeActionStage, setActiveActionStage] = useState(null);
  // v3.15-933: per-action "Last Sent" map { fee_disclosure: { timestamp, to, ... } }
  const [lastSent, setLastSent] = useState({});

  // Fetch config stages for this industry
  useEffect(() => {
    const industry = companyIndustry || 'general';
    console.log("DealStagesChecklist: fetching stages for industry:", industry, "companyIndustry prop:", companyIndustry);
    api.get(`/field-configs/${industry}`).then(res => {
      const stages = res.data?.deal_stages;
      console.log("DealStagesChecklist: received", stages?.length, "stages for industry:", res.data?.industry_category);
      if (stages && stages.length > 0) {
        setConfigStages(stages.filter(s => s.enabled));
      } else {
        setConfigStages(null);
      }
    }).catch((err) => {
      console.error("DealStagesChecklist: failed to fetch stages:", err);
      setConfigStages(null);
    });
  }, [companyIndustry]);

  const activeStages = configStages || FALLBACK_STAGES;

  // v3.15-574: Universal stage order enforcement (config-driven)
  const SOLAR_EXIT_BRANCH = new Set(["approved", "declined"]);

  const isStageUnlocked = (stageKey) => {
    // All stages are always unlocked — no sequential enforcement
    return true;
  };

  const fetchStages = useCallback(async () => {
    try {
      const res = await api.get(`/clients/${clientId}/deal-stages`);
      setStageData(res.data.stages || {});
    } catch (err) {
      console.error('Failed to fetch deal stages:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchStages(); }, [fetchStages]);

  // v3.15-933: fetch the per-action "Last Sent" map so we can render
  // "Sent 2 hrs ago to client@example.com" next to each Send button.
  const fetchLastSent = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/email/last-sent/${clientId}`);
      setLastSent(res.data?.by_action || {});
    } catch (err) {
      // Non-fatal — UI just hides the stamp.
      console.debug('last-sent fetch failed', err?.response?.status);
    }
  }, [clientId]);
  useEffect(() => { fetchLastSent(); }, [fetchLastSent]);

  // v3.14-379: Fetch manager approval alerts for this client
  useEffect(() => {
    if (!clientId || !isInternal) return;
    api.get(`/clients/${clientId}/alerts`).then(res => {
      const alerts = res.data?.alerts || [];
      const ma = alerts.find(a => a.alert_type === 'manager_approval_delayed') ||
                 alerts.find(a => a.alert_type === 'manager_approval_pending');
      setManagerAlert(ma || null);
    }).catch(() => setManagerAlert(null));
  }, [clientId, isInternal, stageData]);

  const [disclosureEmailOpen, setDisclosureEmailOpen] = useState(false);

  const handleToggle = async (stageKey, currentCompleted, stage) => {
    // For disclosure email stage — use disclosure-specific email modal
    if (stageKey === 'send_disclosure' && !currentCompleted) {
      setDisclosureEmailOpen(true);
      return;
    }
    // For app_sent stage — open App Link modal with extension input
    if (stageKey === 'app_sent' && !currentCompleted) {
      setAppLinkOpen(true);
      return;
    }
    // For other email stages (ACH), open ACH modal
    if (stage.type === 'email' && !currentCompleted) {
      setAchEmailOpen(true);
      return;
    }
    // For popup stages, open disclosure modal instead
    if (stage.type === 'popup' && !currentCompleted) {
      setActivePopupStage(stage);
      setDisclosureOpen(true);
      return;
    }

    const newCompleted = !currentCompleted;

    // Required fields validation removed — all stages clickable

    setSaving(stageKey);
    try {
      const payload = {
        stage_key: stageKey,
        completed: newCompleted,
      };
      // Pass maps_to_stage so backend updates client.deal_stage
      if (stage.maps_to_stage && newCompleted) {
        payload.maps_to_stage = stage.maps_to_stage;
      }
      // Pass exclusive_with for server-side validation
      if (stage.exclusive_with) {
        payload.exclusive_with = stage.exclusive_with;
      }
      // Pass required_fields for server-side validation
      if (stage.required_fields) {
        payload.required_fields = stage.required_fields;
      }

      await api.put(`/clients/${clientId}/deal-stages`, payload);
      setStageData(prev => ({
        ...prev,
        [stageKey]: {
          ...prev[stageKey],
          completed: newCompleted,
          updated_at: new Date().toISOString(),
          ...(newCompleted ? { checked_at: new Date().toISOString() } : {}),
        }
      }));
      if (stage.maps_to_stage && newCompleted) {
        toast.success(`Stage updated → ${stage.label}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        console.error('Failed to update stage:', err);
      }
    } finally { setSaving(null); }
  };

  const handleDisclosureConfirm = async () => {
    if (!activePopupStage) return;
    setSaving(activePopupStage.key);
    try {
      await api.put(`/clients/${clientId}/deal-stages`, {
        stage_key: activePopupStage.key, completed: true,
      });
      setStageData(prev => ({
        ...prev,
        [activePopupStage.key]: { ...prev[activePopupStage.key], completed: true, updated_at: new Date().toISOString() }
      }));
      toast.success('Disclosure confirmed');
    } catch (err) {
      console.error('Failed to confirm disclosure:', err);
    } finally {
      setSaving(null);
      setDisclosureOpen(false);
      setActivePopupStage(null);
    }
  };

  const handleThreeWayChange = async (stageKey, value) => {
    setSaving(stageKey);
    try {
      await api.put(`/clients/${clientId}/deal-stages`, {
        stage_key: stageKey, completed: value === 'Yes', three_way_value: value,
      });
      setStageData(prev => ({
        ...prev,
        [stageKey]: { ...prev[stageKey], completed: value === 'Yes', three_way_value: value, updated_at: new Date().toISOString() }
      }));
    } catch (err) {
      console.error('Failed to update three-way stage:', err);
    } finally { setSaving(null); }
  };

  const handleReasonSave = async (stageKey, reason) => {
    try {
      await api.put(`/clients/${clientId}/deal-stages`, {
        stage_key: stageKey, completed: stageData[stageKey]?.completed || false, reason,
      });
      setStageData(prev => ({ ...prev, [stageKey]: { ...prev[stageKey], reason } }));
    } catch (err) {
      console.error('Failed to save reason:', err);
    }
  };

  const handleFollowUpSave = async (stageKey, date, time) => {
    try {
      await api.put(`/clients/${clientId}/deal-stages`, {
        stage_key: stageKey, completed: stageData[stageKey]?.completed || false, follow_up_date: date, follow_up_time: time,
      });
      setStageData(prev => ({ ...prev, [stageKey]: { ...prev[stageKey], follow_up_date: date, follow_up_time: time } }));
    } catch (err) {
      console.error('Failed to save follow-up:', err);
    }
  };

  const handleDepositDateSave = async (stageKey, date) => {
    try {
      await api.put(`/clients/${clientId}/deal-stages`, {
        stage_key: stageKey, completed: stageData[stageKey]?.completed || true, deposit_date: date,
      });
      setStageData(prev => ({ ...prev, [stageKey]: { ...prev[stageKey], deposit_date: date } }));
    } catch (err) {
      console.error('Failed to save deposit date:', err);
    }
  };

  const handleACHEmailSent = () => { fetchStages(); };
  const handleDisclosureEmailSent = () => { fetchStages(); setDisclosureEmailOpen(false); };

  const handleReset = async () => {
    if (!window.confirm('Reset all Deal Stages? This will clear all checkboxes, reasons, and dates.')) return;
    setResetting(true);
    try {
      await api.delete(`/clients/${clientId}/deal-stages`);
      setStageData({});
    } catch (err) {
      console.error('Failed to reset stages:', err);
    } finally { setResetting(false); }
  };

  const completedCount = activeStages.filter(s => {
    const d = stageData[s.key];
    // v3.15-742: three_way_value is the ONLY source of truth
    if (s.type === 'funded_date') return !!client?.funded_date;
    return d?.three_way_value === 'Yes';
  }).length;

  const isFunded = stageData.funds_deposited?.three_way_value === 'Yes';
  const hasAnyData = Object.keys(stageData).length > 0;
  const isSolarExit = client?.industry_category === 'solar_exit';

  if (loading) return <Card className="h-full"><CardContent className="p-4 text-xs text-slate-400">Loading stages...</CardContent></Card>;

  return (
    <>
      <Card className="h-full" data-testid="deal-stages-block">
        <CardHeader className="pb-2 pt-3 px-3">
          <CardTitle className="text-sm font-semibold flex items-center justify-between text-slate-700">
            <span className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4 text-slate-500" />
              Deal Stages
            </span>
            <span className="flex items-center gap-2">
              {hasAnyData && isInternal && (
                <button onClick={handleReset} disabled={resetting} className="text-slate-400 hover:text-red-500 transition-colors" title="Reset all stages" data-testid="reset-deal-stages-btn">
                  <RotateCcw className={`h-3.5 w-3.5 ${resetting ? 'animate-spin' : ''}`} />
                </button>
              )}
              <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${isFunded ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {completedCount}/{activeStages.length}
              </span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {/* Solar Exit commission notice */}
          {isSolarExit && (
            <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded mb-2 text-xs font-medium ${isFunded ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="solar-exit-notice">
              {isFunded ? '$9 earned — payable upon approval' : 'Commission pending — payable upon funding'}
            </div>
          )}
          {/* v3.14-379: Manager Approval Alert Banner — only when manager_approval is a configured stage */}
          {managerAlert && activeStages.some(s => s.key === 'manager_approval') && (
            <div
              data-testid="manager-approval-alert"
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded mb-2 text-xs font-medium ${
                managerAlert.alert_type === 'manager_approval_delayed'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}
            >
              <AlertTriangle className={`h-3.5 w-3.5 flex-shrink-0 ${
                managerAlert.alert_type === 'manager_approval_delayed' ? 'text-red-500' : 'text-amber-500'
              }`} />
              {managerAlert.message}
            </div>
          )}
          <div className="space-y-1" data-testid="deal-stages-list">
            {activeStages.map((stage) => {
              const data = stageData[stage.key] || {};
              // v3.15-742: `completed` flag is no longer authoritative — three_way_value is the single source of truth.
              // `completed` is still written by the API for backward compat but no longer read for display.
              const isSaving = saving === stage.key;

              // ---- Three-way selector stage ----
              if (stage.type === 'three_way') {
                const twValue = data.three_way_value || null;
                // v3.15-931: Appointment Scheduled stage supports a "Booked"
                // state set automatically by the backend when a client
                // self-books from the public link. Renders a distinct green
                // "Booked ✓" badge with a tooltip.
                const isBookedStage = stage.key === 'appointment_scheduled' && twValue === 'Booked';
                return (
                  <div key={stage.key} className="border-b border-slate-100 last:border-0 pb-1.5 last:pb-0" data-testid={`stage-${stage.key}`}>
                    <div className="flex items-center justify-between gap-2 py-1">
                      <span className={`text-xs flex-1 min-w-0 flex items-center flex-wrap gap-1.5 ${twValue === 'Yes' || isBookedStage ? 'text-slate-700 font-medium' : 'text-slate-600'}`}>
                        <span>{stage.label}</span>
                        {/* v3.15-1118: Funding Confirmed auto-date stamp. Only
                            renders for the "funded" stage when the client has
                            a stored funding_confirmed_date. The w-full forces
                            this onto its own flex-wrap row, placing it directly
                            beneath the stage title in a subtle gray italic. */}
                        {stage.key === 'funded' && client?.funding_confirmed_date && (
                          <span
                            className="w-full text-[10px] text-slate-500 italic"
                            data-testid="funding-confirmed-date-stamp"
                            title={`Original funding confirmation: ${new Date(client.funding_confirmed_date).toLocaleString()}`}
                          >
                            Confirmed {(() => {
                              try {
                                return new Date(client.funding_confirmed_date).toLocaleDateString('en-US', {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                });
                              } catch { return client.funding_confirmed_date; }
                            })()}
                          </span>
                        )}
                        {/* v3.18-1310 — Completion timestamp for Loan Docs
                            Signed + Lender Review & Underwriting.  Renders
                            beneath the stage label using the existing muted
                            helper-text styling, only when the stage is
                            currently ``Yes`` AND the backend has stamped
                            ``checked_at``.  ``w-full`` inside the flex-wrap
                            parent forces this onto its own row.  Persists
                            across refresh (comes straight from
                            ``/deal-stages`` GET).  Includes employee name
                            attribution when the backend provides one. */}
                        {/* v3.19-1415 1PM UPDATE — universal completion stamp
                            (Date • Time • User • Auto/Manual chip) rendered
                            on EVERY three_way "Yes" stage.  Reads the new
                            `first_completed_*` fields written by
                            services/deal_stage_workflow.py.  Falls back to
                            legacy `checked_at` for stages predating the SSOT.
                            The Legacy docs_complete / docs_underwriting-only
                            stamp below is now redundant but kept for backward
                            compatibility until every deal has a
                            first_completed_at stamp. */}
                        {twValue === 'Yes' && (data.first_completed_at || data.checked_at) && (() => {
                          try {
                            const iso = data.first_completed_at || data.checked_at;
                            const d = new Date(iso);
                            const datePart = data.first_completed_date_et
                              || d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            const timePart = data.first_completed_time_et
                              || d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
                            const who = data.first_completed_by_name || data.updated_by_name || '';
                            const source = data.first_completed_source || data.last_source || null;
                            const isAuto = source === 'automatic';
                            return (
                              <span
                                className="w-full inline-flex items-center flex-wrap gap-1 text-[10px] text-slate-500 italic"
                                data-testid={`stage-completion-stamp-${stage.key}`}
                                title={`Completion recorded ${d.toLocaleString()}${who ? ` by ${who}` : ''}${source ? ` (${source})` : ''}`}
                              >
                                <span>Completed {datePart} · {timePart}{who && !isAuto ? ` · ${who}` : ''}</span>
                                {isAuto && (
                                  <span
                                    className="not-italic px-1 py-[1px] rounded bg-sky-50 text-sky-700 border border-sky-200 text-[9px] font-semibold uppercase tracking-wider"
                                    data-testid={`stage-auto-chip-${stage.key}`}
                                  >
                                    Auto
                                  </span>
                                )}
                              </span>
                            );
                          } catch { return null; }
                        })()}
                        {false && (stage.key === 'docs_complete' || stage.key === 'docs_underwriting')
                          && twValue === 'Yes'
                          && data.checked_at && (() => {
                            try {
                              const d = new Date(data.checked_at);
                              const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                              const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
                              return (
                                <span
                                  className="w-full text-[10px] text-slate-500 italic"
                                  data-testid={`stage-completion-stamp-${stage.key}`}
                                  title={`Completion recorded ${d.toLocaleString()}${data.updated_by_name ? ` by ${data.updated_by_name}` : ''}`}
                                >
                                  Completed {datePart} · {timePart}{data.updated_by_name ? ` · ${data.updated_by_name}` : ''}
                                </span>
                              );
                            } catch { return null; }
                          })()}
                        {isBookedStage && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"
                            title={[
                              data.booking_display || 'Booked',
                              data.booking_rep_name ? `with ${data.booking_rep_name}` : '',
                              data.booked_at ? `Booked on ${new Date(data.booked_at).toLocaleString()}` : '',
                            ].filter(Boolean).join('\n')}
                            data-testid={`stage-booked-badge-${stage.key}`}
                          >
                            Booked ✓
                            {data.booking_starts_at && (
                              <span className="font-normal text-emerald-600" data-testid={`stage-booked-when-${stage.key}`}>
                                — {(() => {
                                  try {
                                    return new Date(data.booking_starts_at).toLocaleString('en-US', {
                                      month: 'short', day: 'numeric', year: 'numeric',
                                      hour: 'numeric', minute: '2-digit', hour12: true,
                                      timeZone: data.booking_timezone || 'America/New_York',
                                    });
                                  } catch { return data.booking_display || ''; }
                                })()}
                                {data.booking_timezone_label ? ` (${data.booking_timezone_label})` : ''}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {/* v3.15-779: inline send button for email-action stages */}
                        {/* v3.15-933: "Sent N ago to ..." stamp, when we have a record. */}
                        {/* v3.21-1785 · Welcome SMS action — the
                            v1784 stage entry declares
                            `sms_action="welcome"`.  Clicking opens the
                            editable Welcome composer; a successful
                            send calls `onStageChange(stage.key, "yes")`
                            so the stage flips to Yes only after SMS
                            success.  Uses the existing
                            LinkDeliveryModal in text-only mode
                            because that modal already routes through
                            the canonical SMS stack — no new SMS
                            system is introduced. */}
                        {stage.sms_action === 'welcome' && (
                          <button
                            type="button"
                            onClick={() => {
                              // v3.21-1787 · Server-side preview.  The final
                              // concierge-first Welcome copy resolves the
                              // Client's first name + assigned Application
                              // Concierge's name / business phone /
                              // extension on the backend so the textarea
                              // opens fully rendered — never `[First Name]`
                              // placeholders.  If any required field is
                              // missing (concierge unassigned, no phone,
                              // no extension) the endpoint returns 409
                              // with a stable `code` that we surface as a
                              // toast and BLOCK the send.
                              setWelcomeSmsMessage('');
                              setWelcomeSmsPreviewError(null);
                              setWelcomeSmsPreviewLoading(true);
                              setWelcomeSmsOpenForStage(stage);
                              api.get(`/clients/${clientId}/welcome-sms/preview`)
                                .then((res) => {
                                  const d = res?.data || {};
                                  setWelcomeSmsMessage(String(d.message || ''));
                                  setWelcomeSmsPreviewError(null);
                                })
                                .catch((err) => {
                                  const d = err?.response?.data?.detail || {};
                                  const code = typeof d === 'object' ? d.code : null;
                                  const msg  = typeof d === 'object'
                                    ? (d.message || 'Unable to prepare the Welcome message.')
                                    : String(d || 'Unable to prepare the Welcome message.');
                                  setWelcomeSmsPreviewError({ code, message: msg });
                                  setWelcomeSmsMessage('');
                                })
                                .finally(() => setWelcomeSmsPreviewLoading(false));
                            }}
                            className="h-6 px-2 text-[10px] rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 flex items-center gap-1 transition-colors"
                            data-testid={`stage-welcome-sms-btn-${stage.key}`}
                            title={data?.completed ? 'Resend Welcome Message' : 'Send Welcome Message'}
                          >
                            <Send className="h-3 w-3" /> {data?.completed ? 'Resend Welcome' : 'Send Welcome'}
                          </button>
                        )}
                        {stage.email_action && lastSent[stage.email_action]?.timestamp && (
                          <span
                            className="text-[10px] text-slate-500 italic"
                            title={`${lastSent[stage.email_action].subject || ''}\n${new Date(lastSent[stage.email_action].timestamp).toLocaleString()}`}
                            data-testid={`stage-last-sent-${stage.key}`}
                          >
                            Sent {formatTimeAgo(lastSent[stage.email_action].timestamp)} to {lastSent[stage.email_action].to}
                          </span>
                        )}
                        {stage.email_action && (
                          <button
                            type="button"
                            onClick={() => {
                              // v3.21-1775 — Client Link Delivery modal
                              // (Email / Text / Email+Text) for the three
                              // canonical link-carrying Deal Stages.  Maps
                              // stage.email_action → link_type; the
                              // backend resolves the canonical URL.
                              const _LINK_TYPE_BY_ACTION = {
                                application_link: 'credit_application',
                                disclosure_ach:   'ach_disclosure',
                                appointment_link: 'appointment',
                              };
                              const lt = _LINK_TYPE_BY_ACTION[stage.email_action];
                              if (lt) setLinkDeliveryLinkType(lt);
                              else { setEditableEmailAction(stage.email_action); setEditableEmailOpen(true); }
                            }}
                            className="h-6 px-2 text-[10px] rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 flex items-center gap-1 transition-colors"
                            data-testid={`stage-link-delivery-btn-${stage.key}`}
                            title="Send link by Email / Text / Email + Text"
                          >
                            <Send className="h-3 w-3" /> Send Link
                          </button>
                        )}
                        {stage.email_action && (
                          <button
                            type="button"
                            onClick={() => { setEditableEmailAction(stage.email_action); setEditableEmailOpen(true); }}
                            className="h-6 px-2 text-[10px] rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 flex items-center gap-1 transition-colors"
                            data-testid={`stage-send-btn-${stage.key}`}
                            title={`Send ${stage.label}`}
                          >
                            <Send className="h-3 w-3" /> Send
                          </button>
                        )}
                        {stage.has_sms && (
                          <button
                            onClick={() => { setActiveActionStage(stage); setStageSmsOpen(true); }}
                            className="text-green-500 hover:text-green-700 transition-colors"
                            title="Send SMS"
                            data-testid={`stage-sms-btn-${stage.key}`}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {stage.has_email && (
                          <button
                            onClick={() => { setActiveActionStage(stage); setStageEmailOpen(true); }}
                            className="text-blue-500 hover:text-blue-700 transition-colors"
                            title="Send Email"
                            data-testid={`stage-email-btn-${stage.key}`}
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <ThreeWaySelector
                          value={twValue}
                          onChange={(v) => handleThreeWayChange(stage.key, v)}
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  </div>
                );
              }

              // ---- Funded date stage (reads existing funded_date) ----
              if (stage.type === 'funded_date') {
                const fundedDate = client?.funded_date;
                return (
                  <div key={stage.key} className="border-b border-slate-100 last:border-0 pb-1.5 last:pb-0" data-testid={`stage-${stage.key}`}>
                    <div className="flex items-center gap-2 py-1">
                      <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center ${
                        fundedDate ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300'
                      }`}>
                        {fundedDate && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-xs ${fundedDate ? 'text-slate-700 font-medium' : 'text-slate-600'}`}>
                          {stage.label}
                        </span>
                        {fundedDate && (
                          <span className="ml-2 text-[10px] text-emerald-600 font-semibold flex items-center gap-1 inline-flex">
                            <Calendar className="h-3 w-3" />
                            {fundedDate}
                          </span>
                        )}
                      </div>
                    </div>
                    {fundedDate && (
                      <div className="ml-6 mb-1">
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="funded-confirmed-badge">
                          FUNDED
                        </span>
                      </div>
                    )}
                  </div>
                );
              }

              // ---- Default: checkbox, email, popup stages ----
              // v3.15-742: three_way_value is the ONLY source of truth. No fallback to `completed`.
              // Data storage: three_way_value in {"Yes","No","N/A"} (existing backend enum).
              // v3.15-931: Appointment Scheduled also supports "Booked" — set by
              // the backend when a client self-books from the public link. We
              // render that as a distinct green "Booked ✓" badge with a tooltip.
              const derivedState = data.three_way_value || null; // "Yes" | "No" | "N/A" | "Booked" | null
              const isBooked = stage.key === 'appointment_scheduled' && derivedState === 'Booked';
              const isYes = derivedState === 'Yes' || isBooked;  // Booked implies Yes
              const isManagerAlertActive = stage.key === 'manager_approval' && managerAlert && !isYes;
              const isEscalated = isManagerAlertActive && managerAlert.alert_type === 'manager_approval_delayed';
              const isLocked = false;
              const isExclusiveBlocked = false;
              const isDisabled = isSaving;
              // For stages that open a modal (email ACH / popup disclosure) on YES,
              // keep existing side effects — route YES through handleToggle; NO/N/A go direct.
              // v3.15-746: Restore NO-triggered action flow — when NO is pressed on a
              // stage that supports communication, save state THEN auto-open the
              // email (or SMS) modal so the rep can immediately send the follow-up.
              const pressState = async (newState) => {
                if (isDisabled) return;
                if (newState === 'Yes' && (stage.type === 'email' || stage.type === 'popup') && !isYes) {
                  // Preserve email/popup modal flows
                  handleToggle(stage.key, isYes, stage);
                  return;
                }
                await handleThreeWayChange(stage.key, newState);
                if (newState === 'No') {
                  if (stage.has_email || stage.type === 'email') {
                    setActiveActionStage(stage);
                    setStageEmailOpen(true);
                  } else if (stage.has_sms) {
                    setActiveActionStage(stage);
                    setStageSmsOpen(true);
                  }
                }
              };
              const btnBase = 'h-5 px-1.5 text-[10px] font-semibold rounded border transition-all';
              const yesCls = (derivedState === 'Yes' || isBooked)
                ? 'bg-emerald-500 border-emerald-500 text-white'
                : 'bg-white border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-600';
              const noCls = derivedState === 'No'
                ? 'bg-red-500 border-red-500 text-white'
                : 'bg-white border-slate-300 text-slate-500 hover:border-red-400 hover:text-red-600';
              const naCls = derivedState === 'N/A'
                ? 'bg-slate-400 border-slate-400 text-white'
                : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600';
              return (
                <div key={stage.key} className={`border-b border-slate-100 last:border-0 pb-1.5 last:pb-0 ${
                  isManagerAlertActive ? (isEscalated ? 'bg-red-50 rounded' : 'bg-amber-50 rounded') : ''
                }`} data-testid={`stage-${stage.key}`}>
                  <div className="flex items-start gap-2 py-1">
                    <div className="flex items-center gap-1 mt-0.5 flex-shrink-0" data-testid={`stage-toggle-${stage.key}`}>
                      <button
                        onClick={() => pressState('Yes')}
                        className={`${btnBase} ${yesCls} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isDisabled}
                        data-testid={`stage-yes-${stage.key}`}
                        title="Mark YES — stage completed"
                      >YES</button>
                      <button
                        onClick={() => pressState('No')}
                        className={`${btnBase} ${noCls} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isDisabled}
                        data-testid={`stage-no-${stage.key}`}
                        title="Mark NO — stage requires action"
                      >NO</button>
                      <button
                        onClick={() => pressState('N/A')}
                        className={`${btnBase} ${naCls} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={isDisabled}
                        data-testid={`stage-na-${stage.key}`}
                        title="Mark N/A — stage not applicable"
                      >N/A</button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs ${
                        derivedState === 'Yes' || isBooked ? 'text-slate-700 font-medium'
                        : isLocked || isExclusiveBlocked ? 'text-slate-400'
                        : isManagerAlertActive ? (isEscalated ? 'text-red-700 font-semibold' : 'text-amber-700 font-semibold')
                        : 'text-slate-600'
                      }`}>
                        {stage.label}
                      </span>
                      {/* v3.15-931: "Booked ✓" badge when a client has actually
                          self-booked from the appointment link. Hover shows the
                          full timestamp + rep. */}
                      {isBooked && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"
                          title={[
                            data.booking_display || 'Booked',
                            data.booking_rep_name ? `with ${data.booking_rep_name}` : '',
                            data.booked_at ? `Booked on ${new Date(data.booked_at).toLocaleString()}` : '',
                          ].filter(Boolean).join('\n')}
                          data-testid={`stage-booked-badge-${stage.key}`}
                        >
                          Booked ✓
                          {data.booking_starts_at && (
                            <span className="ml-1 font-normal text-emerald-600" data-testid={`stage-booked-when-${stage.key}`}>
                              — {(() => {
                                try {
                                  return new Date(data.booking_starts_at).toLocaleString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric',
                                    hour: 'numeric', minute: '2-digit', hour12: true,
                                    timeZone: data.booking_timezone || 'America/New_York',
                                  });
                                } catch { return data.booking_display || ''; }
                              })()}
                              {data.booking_timezone_label ? ` (${data.booking_timezone_label})` : ''}
                            </span>
                          )}
                        </span>
                      )}
                      {/* Show timestamp for checkbox→stage mapped stages */}
                      {stage.maps_to_stage && derivedState === 'Yes' && !isBooked && data.checked_at && (
                        <span className="ml-1.5 text-[10px] text-emerald-600 font-medium">
                          {new Date(data.checked_at).toLocaleDateString()}
                        </span>
                      )}
                      {stage.type === 'email' && derivedState === 'Yes' && data.email_sent_to && (
                        <span className="ml-1.5 text-[10px] text-blue-500">(sent to {data.email_sent_to})</span>
                      )}
                    </div>
                    {stage.type === 'email' && isInternal && (
                      <Mail className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${isYes ? 'text-emerald-500' : 'text-slate-400'}`} />
                    )}
                    {stage.type === 'popup' && isInternal && (
                      <FileText className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${isYes ? 'text-emerald-500' : 'text-amber-500'}`} />
                    )}
                    {stage.has_sms && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveActionStage(stage); setStageSmsOpen(true); }}
                        className="text-green-500 hover:text-green-700 transition-colors flex-shrink-0 mt-0.5"
                        title="Send SMS"
                        data-testid={`stage-sms-btn-${stage.key}`}
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {stage.has_email && stage.type !== 'email' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveActionStage(stage); setStageEmailOpen(true); }}
                        className="text-blue-500 hover:text-blue-700 transition-colors flex-shrink-0 mt-0.5"
                        title="Send Email"
                        data-testid={`stage-email-btn-${stage.key}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Reason field */}
                  {stage.has_reason && !isYes && data.updated_at && isInternal && (
                    <div className="ml-6 mb-1">
                      <input type="text" placeholder="Reason..." defaultValue={data.reason || ''}
                        onBlur={(e) => { if (e.target.value !== (data.reason || '')) handleReasonSave(stage.key, e.target.value); }}
                        onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                        className="w-full text-[11px] border border-slate-200 rounded px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
                        data-testid={`stage-reason-${stage.key}`}
                      />
                    </div>
                  )}
                  {stage.has_reason && !isYes && data.reason && !isInternal && (
                    <div className="ml-6 mb-1"><p className="text-[11px] text-slate-500">Reason: {data.reason}</p></div>
                  )}

                  {/* Follow-up */}
                  {stage.has_follow_up && !isYes && data.updated_at && isInternal && (
                    <div className="ml-6 mb-1 flex items-center gap-2">
                      <label className="text-[10px] text-slate-400">Follow-up:</label>
                      <input type="date" defaultValue={data.follow_up_date || ''}
                        onBlur={(e) => handleFollowUpSave(stage.key, e.target.value, data.follow_up_time || '')}
                        className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
                        data-testid={`stage-followup-date-${stage.key}`}
                      />
                      <input type="time" defaultValue={data.follow_up_time || ''}
                        onBlur={(e) => handleFollowUpSave(stage.key, data.follow_up_date || '', e.target.value)}
                        className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
                        data-testid={`stage-followup-time-${stage.key}`}
                      />
                    </div>
                  )}
                  {stage.has_follow_up && !isYes && data.follow_up_date && !isInternal && (
                    <div className="ml-6 mb-1"><p className="text-[11px] text-slate-500">Follow-up: {data.follow_up_date} {data.follow_up_time || ''}</p></div>
                  )}

                  {/* Deposit date */}
                  {stage.has_deposit_date && isYes && (
                    <div className="ml-6 mb-1 flex items-center gap-2">
                      <label className="text-[10px] text-slate-400">Deposit Date:</label>
                      {isInternal ? (
                        <input type="date" defaultValue={data.deposit_date || ''}
                          onBlur={(e) => handleDepositDateSave(stage.key, e.target.value)}
                          className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-300"
                          data-testid={`stage-deposit-date-${stage.key}`}
                        />
                      ) : (
                        <span className="text-[11px] text-slate-600">{data.deposit_date || '-'}</span>
                      )}
                    </div>
                  )}

                  {/* Funded badge for final stage */}
                  {stage.is_final && isYes && (
                    <div className="ml-6 mb-1">
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-testid="funded-confirmed-badge">FUNDED</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ACH Email Modal */}
      <ACHEmailModal
        open={achEmailOpen}
        onClose={() => setAchEmailOpen(false)}
        client={client}
        clientId={clientId}
        onSent={handleACHEmailSent}
      />

      {/* Disclosure Script Popup Modal */}
      <DisclosureScriptModal
        open={disclosureOpen}
        onClose={() => { setDisclosureOpen(false); setActivePopupStage(null); }}
        clientId={clientId}
        client={client}
        onConfirm={handleDisclosureConfirm}
        isInternal={isInternal}
      />

      {/* Disclosure Email Modal */}
      <DisclosureEmailModal
        open={disclosureEmailOpen}
        onClose={() => setDisclosureEmailOpen(false)}
        clientId={clientId}
        client={client}
        onSent={handleDisclosureEmailSent}
      />

      {/* Stage SMS Modal */}
      <StageSMSModal
        open={stageSmsOpen}
        onClose={() => { setStageSmsOpen(false); setActiveActionStage(null); }}
        client={client}
        clientId={clientId}
        stage={activeActionStage}
        onSent={fetchStages}
      />

      {/* Stage Email Modal */}
      <StageEmailModal
        open={stageEmailOpen}
        onClose={() => { setStageEmailOpen(false); setActiveActionStage(null); }}
        client={client}
        clientId={clientId}
        stage={activeActionStage}
        onSent={fetchStages}
      />

      {/* App Link Modal (Solar Exit) */}
      <AppLinkModal
        open={appLinkOpen}
        onClose={() => setAppLinkOpen(false)}
        client={client}
        clientId={clientId}
        onSent={fetchStages}
      />

      {/* v3.15-779: Editable stage-email modal (Fee Disclosure / App Link / Disclosure ACH) */}
      <EditableStageEmailModal
        open={editableEmailOpen}
        onOpenChange={setEditableEmailOpen}
        action={editableEmailAction}
        client={client}
        currentUserEmail={currentUserEmail}
        onSent={() => { setEditableEmailOpen(false); fetchStages(); fetchLastSent(); }}
      />

      {/* v3.21-1775: Client Link Delivery modal — Email / Text / Email + Text
          for Credit Application, ACH / Disclosure, and Appointment links.
          Sending an appointment link here does NOT flip
          "Appointment Scheduled = Yes" — that remains driven by the
          existing scheduling workflow. */}
      <LinkDeliveryModal
        open={!!linkDeliveryLinkType}
        linkType={linkDeliveryLinkType}        client={client}
        clientId={clientId}
        onClose={() => setLinkDeliveryLinkType(null)}
        onSent={() => { fetchStages(); fetchLastSent(); }}
      />

      {/* v3.21-1785 · Welcome SMS composer for the welcome_message_sent
          stage.  In-Hub modal — no new tab, no /login, no navigation. */}
      <Dialog open={!!welcomeSmsOpenForStage} onOpenChange={(v) => { if (!v) setWelcomeSmsOpenForStage(null); }}>
        <DialogContent className="max-w-lg" data-testid="welcome-sms-modal">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-emerald-600" />
              Send Welcome Message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Client</Label>
              <div className="text-slate-800" data-testid="welcome-sms-client">{client?.full_name || '—'}</div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Mobile</Label>
              <div className="text-slate-800 font-mono text-xs" data-testid="welcome-sms-mobile">
                {(() => {
                  const raw = String(client?.phone || '').replace(/\D/g, '');
                  return raw ? `xxx-xxx-${raw.slice(-4)}` : 'No valid mobile on file';
                })()}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Message</Label>
              {welcomeSmsPreviewLoading ? (
                <div className="w-full min-h-[140px] border rounded p-2 text-xs text-slate-500 flex items-center justify-center">
                  Preparing message…
                </div>
              ) : welcomeSmsPreviewError ? (
                <div
                  className="w-full min-h-[100px] border border-amber-200 bg-amber-50 rounded p-2 text-xs text-amber-800"
                  data-testid="welcome-sms-block-reason"
                  data-block-code={welcomeSmsPreviewError.code || 'unknown'}
                >
                  <div className="font-semibold mb-1">Cannot send Welcome message</div>
                  {welcomeSmsPreviewError.message}
                </div>
              ) : (
                <textarea
                  className="w-full min-h-[140px] border rounded p-2 text-sm"
                  value={welcomeSmsMessage}
                  onChange={(e) => setWelcomeSmsMessage(e.target.value)}
                  data-testid="welcome-sms-message"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="h-8 px-3 text-xs rounded border border-slate-200 bg-white hover:bg-slate-50"
                onClick={() => setWelcomeSmsOpenForStage(null)}
                disabled={welcomeSmsSending}
                data-testid="welcome-sms-cancel"
              >Cancel</button>
              <button
                type="button"
                className="h-8 px-3 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                disabled={
                  welcomeSmsSending
                  || welcomeSmsPreviewLoading
                  || !!welcomeSmsPreviewError
                  || !welcomeSmsMessage
                  || !(client?.phone || '').replace(/\D/g, '')
                }
                onClick={async () => {
                  const _stage = welcomeSmsOpenForStage;
                  const phone = String(client?.phone || '').replace(/\D/g, '');
                  if (!phone) { toast.error('No valid mobile number is available for this Client.'); return; }
                  if (!welcomeSmsMessage) {
                    toast.error(welcomeSmsPreviewError?.message || 'Unable to prepare the Welcome message.');
                    return;
                  }
                  setWelcomeSmsSending(true);
                  try {
                    // v3.21-1786 · Explicit success contract — the
                    // canonical `_gate_and_send` returns HTTP 200
                    // even on provider failure with `{ok: false,
                    // outcome: "failed"}` (see `routes/sms.py`
                    // v3.21-1696).  We must inspect the canonical
                    // result flag before flipping the Deal Stage,
                    // otherwise a provider failure would falsely
                    // mark `welcome_message_sent = YES`.
                    // v3.21-1787 · Body message is informational only —
                    // server resolves the concierge-first copy
                    // authoritatively; frontend never dictates it.
                    const _resp = await api.post(`/clients/${clientId}/send-welcome-sms`, {
                      phone: client?.phone || null,
                      message: welcomeSmsMessage,
                      // v3.21-1818 · Welcome SMS is a first-contact
                      // onboarding message — NOT a document/email
                      // resend — so it must not require the resend
                      // ``reason_code`` or ``email_first_attempted``
                      // gates.  Backend now scopes those gates to
                      // credit_app / ach / appointment only.
                      confirm: true,
                    });
                    const _data = _resp?.data || {};
                    const _sendSucceeded = (_data.ok === true) && (_data.outcome !== 'failed');
                    if (!_sendSucceeded) {
                      toast.error(_data.provider_error || _data.detail || 'Unable to send welcome message.');
                      return;
                    }
                    toast.success('Welcome message sent.');
                    // v3.21-1787 · WHITE-SCREEN GUARD.  Update local
                    // stage state DIRECTLY (no parent callback) and
                    // close the modal BEFORE any refetch.  This
                    // avoids the previous cascade where an awaited
                    // parent callback + immediate `fetchStages()`
                    // could re-render the still-mounted modal with
                    // stale/undefined props during Radix unmount and
                    // land in `AppErrorBoundary` as a blank page.
                    if (_stage?.key) {
                      setStageData((prev) => ({
                        ...(prev || {}),
                        [_stage.key]: {
                          ...(prev && prev[_stage.key] ? prev[_stage.key] : {}),
                          completed: true,
                          status: 'yes',
                        },
                      }));
                    }
                    setWelcomeSmsOpenForStage(null);
                    // Refresh AFTER modal unmount — decoupled so a
                    // slow / failing refetch cannot destroy the
                    // Client workspace.
                    setTimeout(() => {
                      try { fetchStages(); } catch { /* noop */ }
                      try { fetchLastSent(); } catch { /* noop */ }
                    }, 0);
                  } catch (err) {
                    const _detail = err?.response?.data?.detail;
                    const _msg = typeof _detail === 'object' ? (_detail?.message || 'Unable to send welcome message.') : (_detail || 'Unable to send welcome message.');
                    toast.error(_msg);
                  } finally {
                    setWelcomeSmsSending(false);
                  }
                }}
                data-testid="welcome-sms-send"
              >{welcomeSmsSending ? 'Sending…' : 'Send Welcome'}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DealStagesChecklist;
